import React, { useState } from 'react';
import Text from '../../../../Components/Base/Text/Text';
import NotificationsSidebarItem from './NotificationsSidebarItem';
import {
	Message,
	useLazyListNotificationsQuery,
	useMarkAllNotificationsAsSeenMutation,
} from '../../../../graphql/graphql';

// @ts-ignore
import styles from './NotificationsSidebar.module.scss';
import { useEffectAsync } from '../../../../helpers';
import Loading from '../../../../Components/Loading';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import Button from '../../../../Components/Button/Button';
import ConfirmationModal from '../../../Base/Components/Confirmation/ConfirmationModal';
import useTranslation from '../../../../Hooks/useTranslation';

interface Props {
	opened: boolean;
	onClose: () => void;
	onUpdate?: () => void;
}

const NotificationsSidebar: React.FC<Props> = props => {
	const { opened, onClose, onUpdate } = props;
	const [getNotifications] = useLazyListNotificationsQuery();
	const [items, setItems] = useState<Message[] | null>(null);
	const [markAllAsSeen] = useMarkAllNotificationsAsSeenMutation();
	const [isMarkAllAsSeenConfirmationOpen, setIsMarkAllAsSeenConfirmationOpen] =
		useState(false);
	const { t } = useTranslation();

	const handleUpdate = async () => {
		onUpdate?.();
		await updateList();
	};

	const updateList = async () => {
		const { items } = await getNotifications().unwrap();

		const notificationItems: Message[] = [];
		for (const item of items?.data || []) {
			if (item) {
				notificationItems.push(item);
			}
		}

		setItems(notificationItems);
	};

	const handleMarkAllAsSeen = async () => {
		const result = await markAllAsSeen().unwrap();
		if (result) {
			await handleUpdate();
		}
		setIsMarkAllAsSeenConfirmationOpen(false);
	};

	useEffectAsync(async () => {
		if (opened) {
			await updateList();
		}
	}, [opened]);

	if (!opened) {
		return <></>;
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.backdrop} />
			<div className={styles.sidebar}>
				<div className={styles.header}>
					<div>
						<Text
							tx={'base.notifications.title'}
							className={styles.title}
							firstUpper
						/>
						<LinkButton
							title='component.notificationsSidebar.markAllAsSeen'
							theme={Theme.BLUE}
							onClick={() => setIsMarkAllAsSeenConfirmationOpen(true)}
						/>
					</div>
					<div className={styles.closeButton} onClick={onClose}>
						<img src='/dist/icons/close.svg' alt='close' />
					</div>
				</div>
				<div className={styles.body}>
					<div className={styles.content}>
						{items === null ? (
							<div style={{ marginTop: '20px' }}>
								<Loading />
							</div>
						) : (
							items.map((item, key) => (
								<NotificationsSidebarItem
									item={item}
									key={key}
									onUpdate={handleUpdate}
								/>
							))
						)}
					</div>
				</div>
			</div>
			{isMarkAllAsSeenConfirmationOpen && (
				<ConfirmationModal
					title='component.notificationsSidebar.confirmationModal.title'
					text={
						t('component.notificationsSidebar.confirmationModal.text') ?? ''
					}
					isOpen
					onClose={() => setIsMarkAllAsSeenConfirmationOpen(false)}>
					<div
						className='d-flex flex-row align-items-center'
						style={{ gap: '15px' }}>
						<Button
							onClick={handleMarkAllAsSeen}
							title='base.yes'
							theme={Theme.GREEN}
						/>
						<LinkButton
							onClick={() => setIsMarkAllAsSeenConfirmationOpen(false)}
							title='base.shared.cancel'
							theme={Theme.RED}
							firstUpper
						/>
					</div>
				</ConfirmationModal>
			)}
		</div>
	);
};

export default NotificationsSidebar;
